/**
 * The Create Study wizard's brain: draft state, debounced autosave, launch and
 * draft deletion. Both editions drive the same machinery — FaceWise Pro from
 * its four steps, FaceWise from its single screen — so it lives here rather
 * than in either screen.
 *
 * What differs between them is only the payload, which the caller supplies as
 * a `LaunchPlan`, and the wording of a failure, which the caller supplies as
 * `describeError` so this file stays out of i18n.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '@/services/baseService'
import { projectService } from '@/services/projectService'
import type { Project } from '@/services/projectTypes'
import { respondentBaseService } from '@/services/respondentBaseService'
import { editableToApiQuestion } from '@/components/questions/apiAdapter'
import type { DraftPatch, StimulusDraft, StudyDraft } from './types'
import type { LaunchPlan } from './launchPlan'
import {
  INITIAL_DRAFT,
  draftHasContent,
  draftToProjectFields,
  hydrateDraft,
  serializeDraft,
} from './draftPersistence'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

/** Upload refusals that a retry cannot fix — the material itself is the problem. */
/** Refusals the backend will give again for the same file, however many times
 *  it is offered: the content check turned it down, it runs longer than the
 *  edition allows, or it is not a file this stimulus can be. Retrying any of
 *  them on every keystroke would re-upload the whole thing and hide the reason
 *  from the person who has to act on it. Everything else is treated as
 *  transient and left for the next save. */
const REFUSED_STIMULUS_CODES = [
  'stimulus_rejected',
  'stimulus_too_long',
  'stimulus_invalid_file',
]

/** A fresh 6-digit access code, pre-filled when the wizard opens. */
const genAccessCode = () => String(Math.floor(100000 + Math.random() * 900000))

type UseWizardDraftOptions = {
  /** Builds the launch payload from the draft as it stands at launch time. */
  buildLaunchPlan: (draft: StudyDraft) => LaunchPlan
  /** Turns a failed launch into a sentence the customer should read. */
  describeError: (err: unknown) => string
  /** Message shown when an existing draft cannot be loaded. */
  draftLoadError: string
  /** Confirmation prompt for deleting a draft, given its name. */
  confirmDelete: (name: string) => string
  /** Fallback message when deleting the draft fails. */
  deleteFailed: string
}

export function useWizardDraft(options: UseWizardDraftOptions) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const draftIdParam = searchParams.get('draft')

  const [draft, setDraft] = useState<StudyDraft>(() => ({ ...INITIAL_DRAFT, accessCode: genAccessCode() }))
  const [launching, setLaunching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shareLink, setShareLink] = useState<string | null>(null)
  // Id of the freshly launched study — "Gotowe" routes to its monitoring page.
  const [launchedId, setLaunchedId] = useState<number | null>(null)

  // --- Draft persistence state ----------------------------------------------
  const [draftId, setDraftId] = useState<number | null>(null)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [deleting, setDeleting] = useState(false)
  // Hydrating an existing draft from the backend (suppresses autosave until done).
  const [hydrating, setHydrating] = useState<boolean>(!!draftIdParam)

  // Refs so the debounced/async save closures always see the latest values
  // without being torn down and recreated on every keystroke.
  const draftRef = useRef(draft)
  const draftIdRef = useRef<number | null>(null)
  const hydratingRef = useRef(hydrating)
  const launchingRef = useRef(false)
  const deletingRef = useRef(false)
  const savingRef = useRef(false)
  const pendingRef = useRef(false)
  const lastSavedRef = useRef<string | null>(null)
  const debounceRef = useRef<number | null>(null)
  // The caller re-creates its callbacks on every render; the launch path reads
  // them through a ref so the sequence below never has to be rebuilt.
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    draftRef.current = draft
  }, [draft])
  useEffect(() => {
    hydratingRef.current = hydrating
  }, [hydrating])

  // Resume an existing draft when opened via /studies/new?draft=<id>.
  useEffect(() => {
    if (!draftIdParam) return
    const id = Number(draftIdParam)
    if (!Number.isFinite(id)) {
      setHydrating(false)
      return
    }
    let cancelled = false
    projectService
      .getProject(id)
      .then((project: Project) => {
        if (cancelled) return
        const hydrated = hydrateDraft(project)
        setDraft(hydrated)
        draftRef.current = hydrated
        setDraftId(id)
        draftIdRef.current = id
        // Mark this exact state as already persisted so we don't immediately
        // re-save an unchanged draft on the first autosave tick.
        lastSavedRef.current = JSON.stringify(serializeDraft(hydrated))
        setSaveState('saved')
      })
      .catch(() => {
        if (!cancelled) setError(optionsRef.current.draftLoadError)
      })
      .finally(() => {
        if (!cancelled) setHydrating(false)
      })
    return () => {
      cancelled = true
    }
  }, [draftIdParam])

  const patchDraft = useCallback((patch: DraftPatch) => setDraft((prev) => ({ ...prev, ...patch })), [])

  // --- Autosave -------------------------------------------------------------
  /** Persist the current wizard state as a draft project. Creates the project
   *  on first save, uploads any new file-backed stimuli once, then stores the
   *  full wizard snapshot in draft_data. Returns the saved project (or null
   *  when there was nothing to save / a launch is in progress). */
  const saveDraft = useCallback(async (): Promise<Project | null> => {
    if (launchingRef.current || hydratingRef.current || deletingRef.current) return null
    const current = draftRef.current
    if (draftIdRef.current == null && !draftHasContent(current)) return null

    const signature = JSON.stringify(serializeDraft(current))
    if (draftIdRef.current != null && signature === lastSavedRef.current) {
      setSaveState('saved')
      return null
    }
    if (savingRef.current) {
      pendingRef.current = true
      return null
    }

    savingRef.current = true
    setSaveState('saving')
    try {
      // 1. Ensure the draft project exists.
      let id = draftIdRef.current
      if (id == null) {
        const created = await projectService.createProject({
          ...draftToProjectFields(current),
          status: 'draft',
          is_active: false,
          draft_data: serializeDraft(current),
        })
        id = created.id
        draftIdRef.current = id
        setDraftId(id)
      }

      // 2. Upload file-backed stimuli that aren't on the server yet (once each),
      //    so the binary survives across a close/resume of the wizard.
      const uploaded: Record<string, { serverId: number; url?: string }> = {}
      // Files the backend will never accept — retrying them forever would just
      // hide the refusal from the person who has to fix it.
      const refused = new Set<string>()
      let order = current.stimuli.length
      for (const stim of current.stimuli) {
        if (!stim.file || stim.serverId) continue
        if (launchingRef.current) break
        try {
          const res = await projectService.uploadStimulus(id, {
            type: stim.type === 'image' ? 'image' : 'video',
            title: stim.name,
            order: order++,
            file: stim.file,
            exposureMs: stim.type === 'image' ? (stim.exposureMs ?? 5000) : undefined,
          })
          uploaded[stim.id] = { serverId: res.id, url: res.url || res.storage_url || stim.url }
        } catch (err) {
          if (err instanceof ApiError && REFUSED_STIMULUS_CODES.includes(err.code ?? '')) {
            refused.add(stim.id)
            setError(optionsRef.current.describeError(err))
          }
          /* anything else is transient — leave this file for the next save */
        }
      }
      if (Object.keys(uploaded).length > 0 || refused.size > 0) {
        setDraft((prev) => ({
          ...prev,
          stimuli: prev.stimuli
            .filter((s) => !refused.has(s.id))
            .map((s) => (uploaded[s.id] ? { ...s, ...uploaded[s.id] } : s)),
        }))
      }

      // 3. Persist the wizard snapshot (with any new server ids) + mirror columns.
      if (launchingRef.current) return null
      const snapshot: StudyDraft = {
        ...current,
        stimuli: current.stimuli
          .filter((s) => !refused.has(s.id))
          .map((s) => (uploaded[s.id] ? { ...s, ...uploaded[s.id] } : s)),
      }
      const saved = await projectService.updateProject(id, {
        ...draftToProjectFields(snapshot),
        status: 'draft',
        is_active: false,
        draft_data: serializeDraft(snapshot),
      })
      lastSavedRef.current = JSON.stringify(serializeDraft(snapshot))
      setSaveState('saved')
      return saved
    } catch {
      setSaveState('error')
      return null
    } finally {
      savingRef.current = false
      if (pendingRef.current && !launchingRef.current && !deletingRef.current) {
        pendingRef.current = false
        if (debounceRef.current) window.clearTimeout(debounceRef.current)
        debounceRef.current = window.setTimeout(() => void saveDraft(), 600)
      }
    }
  }, [])

  // Debounced autosave whenever the draft changes (after hydration completes).
  useEffect(() => {
    if (hydrating || launching || shareLink) return
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => void saveDraft(), 1000)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [draft, hydrating, launching, shareLink, saveDraft])

  /** Update one stimulus in place. Functional so a late-arriving measurement
   *  (a video length read in the background) merges into whatever the customer
   *  has typed since, instead of restoring a stale copy of the draft. */
  const patchStimulus = useCallback((localId: string, patch: Partial<StimulusDraft>) => {
    setDraft((prev) => ({
      ...prev,
      stimuli: prev.stimuli.map((s) => (s.id === localId ? { ...s, ...patch } : s)),
    }))
  }, [])

  /** Drop a stimulus from the draft, and from the draft project when autosave
   *  has already uploaded it. Without the DELETE a customer who swaps their
   *  material launches a study carrying both versions. */
  const removeStimulus = useCallback((localId: string) => {
    const stim = draftRef.current.stimuli.find((s) => s.id === localId)
    setDraft((prev) => ({ ...prev, stimuli: prev.stimuli.filter((s) => s.id !== localId) }))
    const projectId = draftIdRef.current
    if (stim?.serverId != null && projectId != null) {
      // Best effort: the study no longer references it, and a stimulus left
      // behind by a failed DELETE must not stop the customer from moving on.
      void projectService.deleteStimulus(projectId, stim.serverId).catch(() => undefined)
    }
  }, [])

  const launch = useCallback(async () => {
    // Launch is authoritative: stop autosave from racing the promotion to active.
    launchingRef.current = true
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    setLaunching(true)
    setError(null)
    // Let any in-flight autosave (already past its launch check) finish writing
    // the draft before we promote it, so it can't revert status back to 'draft'.
    for (let i = 0; i < 50 && savingRef.current; i++) {
      await new Promise((resolve) => window.setTimeout(resolve, 20))
    }
    const current = draftRef.current
    const plan = optionsRef.current.buildLaunchPlan(current)
    try {
      // Questions built in the wizard (plus the metryczka) are persisted as the
      // study's own questionnaire; they take precedence over a template picked
      // from the side card. This per-study questionnaire is flagged is_hidden so
      // it never appears in the shared templates library — library templates are
      // only ever created manually.
      let questionnaireTemplateId = plan.fields.questionnaire_template ?? null
      if (plan.questions.length > 0) {
        const template = await projectService.createQuestionnaireTemplate({
          name: `${plan.fields.name} — questionnaire`,
          instructions: '',
          interpretation: '',
          is_hidden: true,
          questions: plan.questions.map(editableToApiQuestion),
        } as never)
        questionnaireTemplateId = template.id
      }
      const fields = { ...plan.fields, questionnaire_template: questionnaireTemplateId }

      // Promote the existing draft (if any) rather than creating a duplicate.
      const project = draftIdRef.current
        ? await projectService.updateProject(draftIdRef.current, {
            ...fields,
            status: 'active',
            is_active: true,
            draft_data: null,
          })
        : await projectService.createProject({ ...fields, status: 'active', is_active: true })

      // Persist any stimuli not already attached to the (draft) project.
      //
      // The study is already live at this point, so a refusal here would leave
      // a study collecting respondents with nothing to show them. Anything that
      // goes wrong puts it back to a draft and re-raises, so the customer lands
      // back in the setup screen with the reason rather than on a live study
      // that plays nothing.
      let order = 0
      try {
        for (const stim of current.stimuli) {
          if (stim.serverId) {
            order++
            continue // already uploaded while saving the draft
          }
          if (stim.file) {
            await projectService.uploadStimulus(project.id, {
              type: stim.type === 'image' ? 'image' : 'video',
              title: stim.name,
              order: order++,
              file: stim.file,
              exposureMs: stim.type === 'image' ? (stim.exposureMs ?? 5000) : undefined,
            })
          } else if (stim.type === 'url' && stim.url?.trim()) {
            await projectService.uploadStimulus(project.id, {
              type: 'url',
              title: stim.name || stim.url,
              order: order++,
              storageUrl: stim.url.trim(),
              // The length the browser read out of the YouTube player. A clip
              // that plays from somebody else's player is the one thing the
              // server cannot measure for itself, so sending what we found is
              // the only way the length cap can apply to it at all — and it is
              // also what makes the study's timeline the right length.
              exposureMs: stim.durationSeconds
                ? Math.round(stim.durationSeconds * 1000)
                : undefined,
            })
          } else if (stim.type === 'figma' && stim.url?.trim()) {
            // Figma prototype (Pro, Z-98). The backend reads the screens and
            // keeps a picture of each here, so this is the slow one of the lot.
            await projectService.uploadStimulus(project.id, {
              type: 'figma',
              title: stim.payload?.trim().slice(0, 200) || stim.url.trim(),
              order: order++,
              storageUrl: stim.url.trim(),
              payload: stim.payload ?? '',
              exposureMs: stim.exposureMs,
              config: { goal_node_id: stim.goalNodeId ?? '' },
            })
          } else if (stim.type === 'text' && stim.payload?.trim()) {
            await projectService.uploadStimulus(project.id, {
              type: 'text',
              title: stim.name,
              order: order++,
              payload: stim.payload,
              exposureMs: stim.exposureMs ?? 5000,
            })
          }
        }
      } catch (stimulusError) {
        try {
          await projectService.updateProject(project.id, { status: 'draft', is_active: false })
          draftIdRef.current = project.id
          setDraftId(project.id)
        } catch {
          /* Rolling back is best-effort: the refusal below is the real news,
             and burying it under a second failure helps nobody. */
        }
        throw stimulusError
      }

      // Fire the first batch of tracked email invitations, if any were chosen.
      const invite = plan.invite
      if (invite && (invite.respondentIds.length > 0 || invite.tagIds.length > 0)) {
        try {
          await respondentBaseService.invite(project.id, {
            respondent_ids: invite.respondentIds,
            tag_ids: invite.tagIds,
          })
        } catch {
          /* The study is live regardless — invite failures are non-fatal here. */
        }
      }

      setLaunching(false)
      if (project.share_token) {
        setLaunchedId(project.id)
        setShareLink(`${window.location.origin}/#/r/${project.share_token}`)
      } else {
        navigate(`/studies/${project.id}`)
      }
    } catch (err) {
      launchingRef.current = false
      setError(optionsRef.current.describeError(err))
      setLaunching(false)
    }
  }, [navigate])

  /** Delete the draft being edited (or auto-created by autosave) and return to
   *  the study list. Blocks autosave first so it can't resurrect the project. */
  const deleteDraft = useCallback(
    async (fallbackName: string) => {
      const id = draftIdRef.current
      if (id == null || deletingRef.current) return
      const name = draftRef.current.name.trim() || fallbackName
      if (!window.confirm(optionsRef.current.confirmDelete(name))) return
      deletingRef.current = true
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
      setDeleting(true)
      // Let any in-flight autosave finish so its PUT can't race our DELETE.
      for (let i = 0; i < 50 && savingRef.current; i++) {
        await new Promise((resolve) => window.setTimeout(resolve, 20))
      }
      try {
        await projectService.deleteProject(id)
        navigate('/dashboard')
      } catch (err) {
        deletingRef.current = false
        setDeleting(false)
        setError(err instanceof Error ? err.message : optionsRef.current.deleteFailed)
      }
    },
    [navigate],
  )

  /** Explicit "Save draft" — flush a save, then return to the study list where
   *  the draft now appears. */
  const saveDraftAndExit = useCallback(async () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    try {
      await saveDraft()
    } finally {
      navigate('/dashboard')
    }
  }, [navigate, saveDraft])

  return {
    draft,
    patchDraft,
    patchStimulus,
    removeStimulus,
    draftId,
    saveState,
    hydrating,
    deleting,
    launching,
    error,
    setError,
    shareLink,
    launchedId,
    launch,
    deleteDraft,
    saveDraftAndExit,
  }
}
